import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Skeleton,
  InputRightElement,
  InputGroup,
  Image,
  SimpleGrid,
  useDisclosure,
  Button,
  Badge,
  Progress,
} from "@chakra-ui/react";
import DatePicker from "react-datepicker";
import calendarIcon from "../../assets/images/onleave/calendar.svg";
import IconCheck from "../../assets/images/booking/check-circle.svg";
import IconRed from "../../assets/images/booking/x-red.svg";
import { useEmployeeKPI } from "../../hooks/useEmployeeKPI";
import { useEmployeeAttendanceMonth } from "../../hooks/useEmployeeAttendanceMonthly";
import { useSearchParams } from "react-router-dom";
import FormInfo from "./form/info";
import FormDetailEmployee from "../../components/form/formInfo";
import FormAttendanceEmployee from "../attendanceSummary/form/info";
import { employeeCall } from "../../api/employee";

const toMonthParam = (d) => {
  if (!d) return undefined;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}-${yyyy}`;
};

const safeNum = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const fmtInt = (n) =>
  Number.isFinite(Number(n)) ? Number(n).toLocaleString("vi-VN") : "0";

const fmt1 = (n) => (Number.isFinite(Number(n)) ? Number(n).toFixed(1) : "0.0");

const fmtPct2 = (n) =>
  Number.isFinite(Number(n)) ? `${Number(n).toFixed(2)}%` : "0.00%";

const StatCard = ({ label, value, sub, onClick, color = "#51607B" }) => (
  <Box
    bg="white"
    border="1px solid"
    borderColor="gray.200"
    borderRadius="12px"
    p="12px"
    minH="72px"
    cursor={onClick ? "pointer" : "default"}
    onClick={onClick}
    _hover={
      onClick
        ? {
            borderColor: "gray.300",
            boxShadow: "sm",
          }
        : undefined
    }
    transition="all 0.2s ease"
  >
    <Text fontSize="12px" color="#718096" fontWeight={600}>
      {label}
    </Text>
    <Text fontSize="18px" fontWeight={800} color="var(--color-info-employee)">
      {value}
    </Text>
    {sub ? (
      <Text fontSize="12px" color={color} mt="2px">
        {sub}
      </Text>
    ) : null}
  </Box>
);

const TableEmployeeKPI = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isOpenDetailEmployee,
    onOpen: onOpenDetailEmployee,
    onClose: onCloseDetailEmployee,
  } = useDisclosure();
  const {
    isOpen: isOpenAttendanceEmployee,
    onOpen: onOpenAttendanceEmployee,
    onClose: onCloseAttendanceEmployee,
  } = useDisclosure();

  const [searchParams] = useSearchParams();
  const employeeId = searchParams.get("employeeId") || "";
  const code = searchParams.get("code") || "";
  const fullName = searchParams.get("fullName") || "";

  const [defaultValues, setDefaultValues] = useState(null);
  const [data, setData] = useState(null);
  const [monthDate, setMonthDate] = useState(new Date());
  const [zIndex, setZIndex] = useState(1);

  const handlePopup = (type) => {
    setDefaultValues({
      code,
      monthDate,
      type,
      fullName,
    });
    onOpen();
  };

  const handleOpenAttendance = () => {
    onOpenAttendanceEmployee();
  };

  const handleOpenDetailEmployee = () => {
    onOpenDetailEmployee();
  };

  const { employeeKPI, isLoading, isRefetching } = useEmployeeKPI(
    code,
    monthDate,
  );

  const attendanceParams = useMemo(() => {
    const p = {};
    if (employeeId) p.employeeId = employeeId;
    if (monthDate) {
      p.month = toMonthParam(monthDate);
      p.year = monthDate.getFullYear();
    }
    return p;
  }, [employeeId, monthDate]);

  const {
    isLoading: isAttendanceLoading,
    isRefetching: isAttendanceRefetching,
    summary,
  } = useEmployeeAttendanceMonth({ ...attendanceParams });

  const totalExpected = safeNum(summary?.totalExpected, 0);
  const totalCredit = safeNum(summary?.totalCredit, 0);

  const attendanceRate = useMemo(() => {
    if (!totalExpected) return 0;
    return totalCredit / totalExpected;
  }, [totalCredit, totalExpected]);

  const attendancePct = safeNum(
    summary?.attendanceRatePct,
    attendanceRate * 100,
  );

  const isWarning = summary?.warning ?? attendanceRate < 0.7;
  const badgeBg = isWarning ? "red.50" : "green.50";
  const badgeColor = isWarning ? "red.600" : "green.600";
  const badgeText = isWarning ? "Không đạt (< 70%)" : "Đạt (≥ 70%)";
  const badgeIcon = isWarning ? IconRed : IconCheck;
  const counts = summary?.counts || {};

  useEffect(() => {
    if (employeeId) {
      const fetchData = async () => {
        try {
          const employee = await employeeCall.get(employeeId);
          if (employee) {
            let newEmployee = {};
            if (employee?.department && employee.department === 3) {
              newEmployee = {
                ...(employee?._id && { id: employee._id }),
                isBrokerDebtCertificate:
                  employee?.isBrokerDebtCertificate || false,
                ...(employee?.code && { code: employee.code }),
                ...(employee?.fullName && { fullName: employee.fullName }),
                ...(employee?.password && { password: employee.password }),
                ...(employee?.phone && { phone: employee.phone }),
                ...(employee?.email && { email: employee.email }),
                ...(employee?.avatar && { avatar: employee.avatar }),
                ...(employee?.jobTitleId && {
                  jobTitle: employee?.jobTitle?.name,
                }),
                ...(employee?.dateOfBirth && {
                  dateOfBirth: employee.dateOfBirth,
                }),
                ...(employee?.gender !== "" &&
                  employee?.gender !== null && { gender: employee.gender }),
                ...(employee?.taxCode !== "" &&
                  employee?.taxCode !== null && { taxCode: employee.taxCode }),
                ...(employee?.identificationNumber && {
                  identificationNumber: employee.identificationNumber,
                }),
                ...(employee?.dateOfIdentification && {
                  dateOfIdentification: employee.dateOfIdentification,
                }),
                ...(employee?.placeOfIdentification && {
                  placeOfIdentification: employee.placeOfIdentification,
                }),
                ...(employee?.permanentAddress && {
                  permanentAddress: employee.permanentAddress,
                }),
                ...(employee?.currentAddress && {
                  currentAddress: employee.currentAddress,
                }),
                ...(employee?.bankAccount && {
                  bankAccount: employee.bankAccount,
                }),
                ...(employee?.bankName && { bankName: employee.bankName }),
                ...(employee?.startTime && { startTime: employee.startTime }),
                ...(employee?.endTime && { endTime: employee.endTime }),
                status: employee?.status,
                ...{ department: employee.department },
                ...(employee?.jobPosition && {
                  jobPosition: employee.jobPosition?.name,
                }),
                ...(employee?.supportDepartment && {
                  supportDepartment: employee.supportDepartment?.name,
                }),
                ...(employee?.businessCategory && {
                  businessCategory: employee.businessCategory?.name,
                }),
                ...(employee?.guaranteeType && {
                  guaranteeType: employee.guaranteeType?.name,
                }),
                ...(employee?.guaranteeDate && {
                  guaranteeDate: employee.guaranteeDate,
                }),
                ...(employee?.depositDate && {
                  depositDate: employee.depositDate,
                }),
                ...(employee?.referralCodeId && {
                  referralCodeId: employee.referralCodeId,
                }),
                ...(employee?.saleBlock && {
                  saleBlock: employee.saleBlock?.name,
                }),
                ...(employee?.saleArea && {
                  saleArea: employee.saleArea?.name,
                }),
                ...(employee?.JobTitleCode && {
                  JobTitleCode: employee.JobTitleCode,
                }),
                ...(employee?.branch && { branch: employee.branch?.name }),
                ...(employee?.leaveBalance && {
                  remainingLeave: employee.leaveBalance?.remainingLeave,
                  totalLeave: employee.leaveBalance?.totalLeave,
                }),
                ...(employee?.referralEmployee && {
                  referralEmployee: {
                    fullName: employee.referralEmployee?.fullName,
                    code: employee.referralEmployee?.code,
                    saleBlock: employee.referralEmployee?.saleBlock?.name,
                    saleArea: employee.referralEmployee?.saleArea?.name,
                  },
                }),
                idCardFrontImage: employee?.idCardFrontImage || "",
                idCardBackImage: employee?.idCardBackImage || "",
                ...(employee?.employee && { employee: employee.employee }),
              };
            } else if (employee?.department) {
              newEmployee = {
                ...(employee?._id && { id: employee._id }),
                isBrokerDebtCertificate:
                  employee?.isBrokerDebtCertificate || false,
                ...(employee?.code && { code: employee.code }),
                ...(employee?.fullName && { fullName: employee.fullName }),
                ...(employee?.password && { password: employee.password }),
                ...(employee?.phone && { phone: employee.phone }),
                ...(employee?.email && { email: employee.email }),
                ...(employee?.avatar && { avatar: employee.avatar }),
                ...(employee?.jobTitleId && {
                  jobTitle: employee?.jobTitle?.name,
                }),
                ...(employee?.dateOfBirth && {
                  dateOfBirth: employee.dateOfBirth,
                }),
                ...(employee?.gender !== "" &&
                  employee?.gender !== null && { gender: employee.gender }),
                ...(employee?.taxCode !== "" &&
                  employee?.taxCode !== null && { taxCode: employee.taxCode }),
                ...(employee?.identificationNumber && {
                  identificationNumber: employee.identificationNumber,
                }),
                ...(employee?.dateOfIdentification && {
                  dateOfIdentification: employee.dateOfIdentification,
                }),
                ...(employee?.placeOfIdentification && {
                  placeOfIdentification: employee.placeOfIdentification,
                }),
                ...(employee?.permanentAddress && {
                  permanentAddress: employee.permanentAddress,
                }),
                ...(employee?.currentAddress && {
                  currentAddress: employee.currentAddress,
                }),
                ...(employee?.bankAccount && {
                  bankAccount: employee.bankAccount,
                }),
                ...(employee?.bankName && { bankName: employee.bankName }),
                ...(employee?.startTime && { startTime: employee.startTime }),
                ...(employee?.endTime && { endTime: employee.endTime }),
                status: employee?.status,
                ...{ department: employee.department },
                ...(employee?.jobPosition && {
                  jobPosition: employee.jobPosition?.name,
                }),
                ...(employee?.supportDepartment && {
                  supportDepartment: employee.supportDepartment?.name,
                }),
                ...(employee?.businessCategory && {
                  businessCategory: employee.businessCategory?.name,
                }),
                ...(employee?.guaranteeType && {
                  guaranteeType: employee.guaranteeType?.name,
                }),
                ...(employee?.guaranteeDate && {
                  guaranteeDate: employee.guaranteeDate,
                }),
                ...(employee?.depositDate && {
                  depositDate: employee.depositDate,
                }),
                ...(employee?.referralCodeId && {
                  referralCodeId: employee.referralCodeId,
                }),
                ...(employee?.saleBlock && {
                  saleBlock: employee.saleBlock?.name,
                }),
                ...(employee?.saleArea && {
                  saleArea: employee.saleArea?.name,
                }),
                ...(employee?.JobTitleCode && {
                  JobTitleCode: employee.JobTitleCode,
                }),
                ...(employee?.branch && { branch: employee.branch?.name }),
                ...(employee?.leaveBalance && {
                  remainingLeave: employee.leaveBalance?.remainingLeave,
                  totalLeave: employee.leaveBalance?.totalLeave,
                }),
                ...(employee?.referralEmployee && {
                  referralEmployee: {
                    fullName: employee.referralEmployee?.fullName,
                    code: employee.referralEmployee?.code,
                    saleBlock: employee.referralEmployee?.saleBlock?.name,
                    saleArea: employee.referralEmployee?.saleArea?.name,
                  },
                }),
                idCardFrontImage: employee?.idCardFrontImage || "",
                idCardBackImage: employee?.idCardBackImage || "",
                ...(employee?.employee && { employee: employee.employee }),
                wrongLockingCount: employee.wrongLockingCount || 0,
              };
            } else {
              newEmployee = {
                ...(employee?._id && { id: employee._id }),
                isBrokerDebtCertificate:
                  employee?.isBrokerDebtCertificate || false,
                ...(employee?.code && { code: employee.code }),
                ...(employee?.fullName && { fullName: employee.fullName }),
                ...(employee?.password && { password: employee.password }),
                ...(employee?.phone && { phone: employee.phone }),
                ...(employee?.email && { email: employee.email }),
                ...(employee?.avatar && { avatar: employee.avatar }),
                ...(employee?.jobTitleId && {
                  jobTitle: employee?.jobTitle?.name,
                }),
                ...(employee?.dateOfBirth && {
                  dateOfBirth: employee.dateOfBirth,
                }),
                ...(employee?.gender !== "" &&
                  employee?.gender !== null && { gender: employee.gender }),
                ...(employee?.taxCode !== "" &&
                  employee?.taxCode !== null && { taxCode: employee.taxCode }),
                ...(employee?.identificationNumber && {
                  identificationNumber: employee.identificationNumber,
                }),
                ...(employee?.dateOfIdentification && {
                  dateOfIdentification: employee.dateOfIdentification,
                }),
                ...(employee?.placeOfIdentification && {
                  placeOfIdentification: employee.placeOfIdentification,
                }),
                ...(employee?.permanentAddress && {
                  permanentAddress: employee.permanentAddress,
                }),
                ...(employee?.currentAddress && {
                  currentAddress: employee.currentAddress,
                }),
                ...(employee?.bankAccount && {
                  bankAccount: employee.bankAccount,
                }),
                ...(employee?.bankName && { bankName: employee.bankName }),
                ...(employee?.startTime && { startTime: employee.startTime }),
                ...(employee?.endTime && { endTime: employee.endTime }),
                status: employee?.status,
                ...{ department: employee.department },
                ...(employee?.jobPosition && {
                  jobPosition: employee.jobPosition?.name,
                }),
                ...(employee?.supportDepartment && {
                  supportDepartment: employee.supportDepartment?.name,
                }),
                ...(employee?.businessCategory && {
                  businessCategory: employee.businessCategory?.name,
                }),
                ...(employee?.guaranteeType && {
                  guaranteeType: employee.guaranteeType?.name,
                }),
                ...(employee?.guaranteeDate && {
                  guaranteeDate: employee.guaranteeDate,
                }),
                ...(employee?.depositDate && {
                  depositDate: employee.depositDate,
                }),
                ...(employee?.referralCode && {
                  referralCode: employee.referralCode?.name,
                }),
                ...(employee?.saleBlock && {
                  saleBlock: employee.saleBlock?.name,
                }),
                ...(employee?.saleArea && {
                  saleArea: employee.saleArea?.name,
                }),
                ...(employee?.JobTitleCode && {
                  JobTitleCode: employee.JobTitleCode,
                }),
                ...(employee?.branch && { branch: employee.branch?.name }),
                ...(employee?.leaveBalance && {
                  remainingLeave: employee.leaveBalance?.remainingLeave,
                  totalLeave: employee.leaveBalance?.totalLeave,
                }),
                idCardFrontImage: employee?.idCardFrontImage || "",
                idCardBackImage: employee?.idCardBackImage || "",
                ...(employee?.employee && { employee: employee.employee }),
                wrongLockingCount: employee.wrongLockingCount || 0,
              };
            }

            setData(newEmployee);
          }
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      };

      fetchData();
    }
  }, [employeeId]);

  return (
    <>
      <Flex alignItems="center" justifyContent="space-between">
        <Flex alignItems="center" gap={{ base: "74px" }}>
          <Text fontSize={{ base: "20px" }} fontWeight={600}>
            {fullName ? `KPI của ${fullName}` : "Danh sách KPI nhân viên"}
          </Text>
        </Flex>

        <Flex alignItems={"center"} gap={"16px"}>
          <Button
            w={"176px"}
            h={"44px"}
            display={"flex"}
            alignItems={"center"}
            p={"10px 16px 10px 12px"}
            gap={"8px"}
            background="var(--linear-gradient-employee)"
            _hover={{
              filter: "brightness(90%)",
            }}
            onClick={handleOpenDetailEmployee}
          >
            <Text
              fontSize={{ base: "15px" }}
              fontWeight={600}
              color="var(--color-main)"
            >
              Thông tin nhân viên
            </Text>
          </Button>
          <Button
            display={"flex"}
            alignItems={"center"}
            p={"10px 16px 10px 12px"}
            gap={"8px"}
            h={"44px"}
            backgroundImage="var(--linear-gradient-excel)"
            _hover={{
              filter: "brightness(90%)",
            }}
            onClick={handleOpenAttendance}
          >
            <Text
              fontSize={{ base: "15px" }}
              fontWeight={600}
              color="var(--color-main)"
            >
              Thông tin chuyên cần
            </Text>
          </Button>
        </Flex>
      </Flex>

      <Box mt={{ base: "23px" }}>
        <Flex flexDirection={"column"} gap={{ base: "16px" }}>
          <Flex
            backgroundColor="var(--color-main)"
            borderRadius={{ base: "12px" }}
            flexDirection={"column"}
          >
            <Flex paddingLeft={4} paddingTop={4}>
              <Text fontSize={{ base: "16px" }} fontWeight={600}>
                Đánh giá KPI:{" "}
                {employeeKPI?.evaluation?.classification?.label ||
                  "Chưa có đánh giá"}
              </Text>
            </Flex>

            <Flex
              alignItems={"center"}
              justifyContent={"space-between"}
              gap={{ base: "32px" }}
              p={"16px 16px 12px 16px"}
            >
              <Flex
                alignItems={"flex-start"}
                gap={{ base: "16px 32px" }}
                flex={"1"}
                flexDirection={"column"}
              >
                <Flex
                  alignItems={"center"}
                  gap={{ base: "16px 32px" }}
                  flex={"1"}
                  flexWrap={"wrap"}
                  w={"100%"}
                >
                  <InputGroup w={"100%"} maxW={"255px"} zIndex={zIndex}>
                    <DatePicker
                      selected={monthDate}
                      onChange={(d) => setMonthDate(d)}
                      showMonthYearPicker
                      dateFormat="MM/yyyy"
                      locale="vi"
                      isClearable
                      placeholderText="Chọn tháng"
                      className="datePickerStyle"
                      onFocus={() => setZIndex(3)}
                      onBlur={() => setZIndex(1)}
                    />
                    {!monthDate && (
                      <InputRightElement pointerEvents="none" h={"44px"}>
                        <Image src={calendarIcon} />
                      </InputRightElement>
                    )}
                  </InputGroup>
                </Flex>
              </Flex>
            </Flex>

            {/* Tổng kết chuyên cần */}
            <Box px="16px" pb="4px">
              <Skeleton
                isLoaded={!isAttendanceLoading && !isAttendanceRefetching}
              >
                <Box
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="14px"
                  p="16px"
                >
                  <Flex
                    align="flex-start"
                    justify="space-between"
                    gap="12px"
                    flexWrap="wrap"
                  >
                    <Box>
                      <Text
                        fontSize="14px"
                        fontWeight={800}
                        color="var(--color-info-employee)"
                      >
                        Tổng kết chuyên cần
                      </Text>
                      <Flex align="center" gap="10px" mt="6px" flexWrap="wrap">
                        <Badge
                          bg={badgeBg}
                          color={badgeColor}
                          border="1px solid"
                          borderColor={isWarning ? "red.200" : "green.200"}
                          borderRadius="999px"
                          px="10px"
                          py="4px"
                          fontSize="12px"
                          fontWeight={800}
                        >
                          <Flex align="center" gap="6px">
                            <Image src={badgeIcon} w="14px" h="14px" />
                            <Text>{badgeText}</Text>
                          </Flex>
                        </Badge>

                        <Text fontSize="12px" color="#51607B">
                          Công:{" "}
                          <Text as="span" fontWeight={800} color="#1A202C">
                            {fmt1(totalCredit)}
                          </Text>
                          {" / "}
                          <Text as="span" fontWeight={800} color="#1A202C">
                            {fmt1(totalExpected)}
                          </Text>
                        </Text>
                      </Flex>
                    </Box>

                    <Box minW="220px">
                      <Text fontSize="12px" color="#718096" fontWeight={700}>
                        Tỉ lệ
                      </Text>
                      <Text
                        fontSize="28px"
                        fontWeight={700}
                        color={isWarning ? "red.600" : "green.600"}
                        lineHeight="1.1"
                      >
                        {fmtPct2(attendancePct)}
                      </Text>
                    </Box>
                  </Flex>

                  <Box mt="12px">
                    <Progress
                      value={Math.min(100, Math.max(0, attendancePct))}
                      size="sm"
                      borderRadius="999px"
                      colorScheme={isWarning ? "red" : "green"}
                    />
                    <Text fontSize="11px" color="#718096" mt="6px">
                      Quy tắc: điểm chuyên cần = tổng công / tổng công chuẩn của
                      tháng. Nếu &lt; 70% sẽ cảnh báo đỏ.
                    </Text>
                  </Box>

                  <SimpleGrid
                    columns={{ base: 2, md: 3, lg: 6 }}
                    spacing="10px"
                    mt="14px"
                  >
                    <StatCard
                      label="Đi làm"
                      value={fmtInt(counts?.checkinDays || 0)}
                    />
                    <StatCard
                      label="Chấm công ngoài"
                      value={fmtInt(counts?.registerOutsideDays || 0)}
                    />
                    <StatCard
                      label="Làm bù"
                      value={fmtInt(counts?.makeupWorkDays || 0)}
                    />
                    <StatCard
                      label="Nghỉ phép"
                      value={fmtInt(counts?.leaveDays || 0)}
                    />
                    <StatCard
                      label="Vắng"
                      value={fmtInt(counts?.absentDays || 0)}
                      sub={isWarning ? "Cần theo dõi" : undefined}
                      color={isWarning ? "red.600" : "#51607B"}
                    />
                    <StatCard
                      label="Ngày nghỉ"
                      value={fmtInt(counts?.holidayDays || 0)}
                    />
                  </SimpleGrid>
                </Box>
              </Skeleton>
            </Box>

            <Box width={"100%"} height={"100%"}>
              <Skeleton isLoaded={!isLoading && !isRefetching}>
                <Box padding={4}>
                  <SimpleGrid
                    columns={{ base: 2, md: 3, lg: 4 }}
                    spacing="10px"
                    w={"100%"}
                  >
                    <StatCard
                      label="Lead"
                      value={fmtInt(employeeKPI?.leadCount || 0)}
                      onClick={() => handlePopup("leads")}
                    />
                    <StatCard
                      label="Tư vấn"
                      value={fmtInt(employeeKPI?.consultCount || 0)}
                      onClick={() => handlePopup("consultations")}
                    />
                    <StatCard
                      label="Giao dịch"
                      value={fmtInt(employeeKPI?.transactionCount || 0)}
                      onClick={() => handlePopup("transactions")}
                    />
                    <StatCard
                      label="Số giao dịch thiếu lịch sử Lead/Tư vấn"
                      value={fmtInt(
                        employeeKPI?.missingLeadOrConsultTransactions?.length ||
                          0,
                      )}
                      onClick={() =>
                        handlePopup("missingLeadOrConsultTransactions")
                      }
                    />
                  </SimpleGrid>
                </Box>
              </Skeleton>
            </Box>
          </Flex>
        </Flex>
      </Box>

      {defaultValues && (
        <FormInfo
          isOpen={isOpen}
          onClose={onClose}
          defaultValues={defaultValues}
        />
      )}

      {data && (
        <FormDetailEmployee
          isOpen={isOpenDetailEmployee}
          onClose={onCloseDetailEmployee}
          defaultValues={data}
        />
      )}

      {employeeId && (
        <FormAttendanceEmployee
          isOpen={isOpenAttendanceEmployee}
          onClose={onCloseAttendanceEmployee}
          defaultValues={{ employeeId }}
        />
      )}
    </>
  );
};

export default TableEmployeeKPI;
